import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { linkTextClasses, Panel, StatCard, StatCardList } from '@evogard/ui';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';

/**
 * Os números de demandas no início da interna.
 *
 * Cada módulo publica os próprios números **pelo próprio contrato**. Um módulo
 * de relatórios lendo as tabelas de cobrança e comercial é exatamente o que a
 * fronteira de módulo proíbe — e é a razão de "diretoria" ser tela, não módulo.
 *
 * O painel não fabrica número: enquanto a consulta está no ar, o lugar do
 * número é um esqueleto, não um zero. Zero é um fato, e afirmá-lo antes de
 * saber é a tela mentindo com aparência de precisão.
 */
export function TicketsPanel() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;

  const query = useQuery({
    queryKey: ['demandas', 'numeros', tenantId],
    queryFn: async () => {
      const r = await api.tickets.numbers();
      return ok(r, 200, 'Não foi possível carregar os números.');
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const n = query.data;

  return (
    <Panel
      title="Tickets"
      /*
       * "nesta base" deixou de ser verdade em 17/09/2026: quem não tem
       * `ver_qualquer` conta o próprio setor, o que abriu e o que responde — o
       * mesmo escopo da mesa, porque `numbers` passou a compartilhar o
       * `scopeOf` dela. A frase acompanha o que o número passou a contar.
       */
      descricao={
        can(context, 'tickets.tickets', 'ver_qualquer')
          ? 'O que está aberto nesta base.'
          : 'O que está aberto no que você acompanha.'
      }
      actions={
        <Link to="/tickets" className={linkTextClasses('text-xs')}>
          Ver todos
        </Link>
      }
    >
      {query.isError ? (
        <p className="text-sm text-texto-suave">
          Não foi possível ler os números agora. A lista continua acessível.
        </p>
      ) : (
        <StatCardList>
          <StatCard icon="demandas" label="Abertos" value={n?.open} />
          {/*
           * `erro` só quando o número É a má notícia: zero vencidas em vermelho
           * seria alarme onde há boa notícia. O tom pinta a pastilha e o número
           * juntos — ver o docblock do `StatCard`.
           */}
          <StatCard
            icon="relogio"
            label="Vencidos"
            value={n?.overdue}
            tone={n && n.overdue > 0 ? 'erro' : undefined}
          />
          <StatCard icon="pessoa" label="Comigo" value={n?.my} />
          <StatCard icon="pessoas" label="Sem responsável" value={n?.withoutAssignee} />
        </StatCardList>
      )}
    </Panel>
  );
}
